/* The header bar uses the same Klaviyo interest property as the AU popup. */
if (!customElements.get('gp-interest-signup')) {
  customElements.define('gp-interest-signup', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.form = this.querySelector('form');
      this.status = this.querySelector('[role="status"]');
      this.email = this.form?.elements.namedItem('email');
      this.submit = this.form?.querySelector('[type="submit"]');
      this.followup = this.querySelector('[data-signup-followup]');
      this.saved = this.querySelector('[data-signup-saved]');
      this.chips = [...this.querySelectorAll('[data-interest]')];
      if (!this.form || !this.status || !this.email || !this.submit || !this.followup || !this.saved) return;
      this.initialized = true;
      this.onSubmit = event => this.subscribe(event);
      // Interests are asked after joining; each change updates the new profile.
      this.onClick = event => {
        const chip = event.target.closest('[data-interest]');
        if (!chip) return;
        chip.setAttribute('aria-pressed', String(chip.getAttribute('aria-pressed') !== 'true'));
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => this.saveInterests(), 500);
      };
      this.form.addEventListener('submit', this.onSubmit);
      this.followup.addEventListener('click', this.onClick);
      this.submit.disabled = false;
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.followup?.removeEventListener('click', this.onClick);
      clearTimeout(this.saveTimer);
      this.request?.abort();
      this.initialized = false;
    }

    endpoint(path) {
      const url = new URL(`https://a.klaviyo.com/client/${path}/`);
      url.searchParams.set('company_id', this.dataset.companyId);
      return url;
    }

    post(path, data, signal) {
      return fetch(this.endpoint(path), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          accept: 'application/vnd.api+json',
          revision: '2026-10-15'
        },
        signal,
        body: JSON.stringify({ data })
      });
    }

    // Nothing ticked means both ranges.
    interests() {
      const picked = this.chips.filter(chip => chip.getAttribute('aria-pressed') === 'true').map(chip => chip.dataset.interest);
      return picked.length ? picked : this.chips.map(chip => chip.dataset.interest);
    }

    // Lets Klaviyo recognise this visitor, so the AU popup does not ask them again.
    identify(email) {
      try {
        if (typeof window.klaviyo?.identify === 'function') window.klaviyo.identify({ email });
        else (window._learnq = window._learnq || []).push(['identify', { $email: email }]);
      } catch (error) { /* Signup has already succeeded. */ }
    }

    async subscribe(event) {
      event.preventDefault();
      if (this.pending || !this.form.reportValidity()) return;
      const email = this.email.value.trim();
      const originalLabel = this.submit.textContent;
      let joined = false;
      this.pending = true;
      this.submit.disabled = true;
      this.submit.textContent = 'Joining…';
      this.form.setAttribute('aria-busy', 'true');
      this.status.hidden = true;
      this.request = new AbortController();
      const timeout = setTimeout(() => this.request.abort(), 15000);
      try {
        if (!this.dataset.companyId || !this.dataset.listId) throw new Error('Missing signup configuration');
        const response = await this.post('subscriptions', {
          type: 'subscription',
          attributes: {
            custom_source: 'AU | Website signup bar',
            profile: { data: { type: 'profile', attributes: {
              email,
              properties: { 'Growplay Interests': this.interests() },
              subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
            } } }
          },
          relationships: { list: { data: { type: 'list', id: this.dataset.listId } } }
        }, this.request.signal);
        if (!response.ok) throw new Error('Signup request failed');
        joined = true;
        this.joinedEmail = email;
        this.identify(email);
        // Once joined, the follow-up question replaces the invitation.
        this.form.hidden = true;
        const intro = this.closest('.growplay-email-signup')?.querySelector('[data-signup-intro]');
        if (intro) intro.hidden = true;
        this.followup.hidden = false;
        if (this.isConnected) this.followup.focus({ preventScroll: true });
      } catch (error) {
        this.status.textContent = 'We couldn’t save your signup. Please try again.';
        this.status.hidden = false;
        if (this.isConnected) this.status.focus({ preventScroll: true });
      } finally {
        clearTimeout(timeout);
        this.pending = false;
        this.form.removeAttribute('aria-busy');
        if (!joined) {
          this.submit.disabled = false;
          this.submit.textContent = originalLabel;
        }
      }
    }

    async saveInterests() {
      this.saveRequest?.abort();
      this.saveRequest = new AbortController();
      this.saved.dataset.state = '';
      this.saved.textContent = '';
      try {
        const response = await this.post('profiles', {
          type: 'profile',
          attributes: { email: this.joinedEmail, properties: { 'Growplay Interests': this.interests() } }
        }, this.saveRequest.signal);
        if (!response.ok) throw new Error('Interest update failed');
        this.saved.dataset.state = 'saved';
        this.saved.textContent = 'Saved';
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.saved.dataset.state = 'error';
        this.saved.textContent = 'Not saved, try again';
      }
    }
  });
}
